import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Spinner, Text } from '@factorialco/deprecated-design-system'
import type { EngagementInsightsEnpsInsight } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import getPercentageValue from 'modules/engagement/lib/getPercentageValue'
import {
  getScore,
  getScoreDescription,
  getScoreRange,
} from 'modules/engagement/lib/netPromoterScore'
import { useEngagementInsightsData } from 'modules/engagement_insights/hooks/useEngagementInsightsData'
import { useEnpsInsight } from 'modules/engagement_insights/hooks/useEnpsInsight'
import { getTagColor, getTagLabel } from 'modules/engagement_insights/lib/surveyStatusTag'
import { getEnpsWidgetEllipseColor } from 'modules/engagement_insights/lib/widget'
import { ParticipantTypes } from 'modules/enps/store/models/Form'

import Chart from '../../Chart'
import AnalyticsDescription from '../../_Widget/_AnalyticsDescription'
import EmptyStatusDescription from '../../_Widget/_EmptyStatusDescription'
import WidgetHeader from '../../_Widget/_Header'

const getUsersLabel = (type: string) => {
  switch (type) {
    case 'detractors':
      return i18n.t('enps.analytics.users.detractors')
    case 'passives':
      return i18n.t('enps.analytics.users.passives')
    case 'promoters':
      return i18n.t('enps.analytics.users.promoters')
    default:
      throw new Error(`Unknown translation for type: ${type}`)
  }
}

const chartColors = {
  detractors: '#FF5858', // Red / 500
  passives: '#F5BA58', // Champagne/1000
  promoters: '#68CC45', // Lime/1000
}

interface ChartBoxProps {
  type: string
  scorePercentage: number
  color: string
  showScore: boolean
}

const ChartBox = ({ type, scorePercentage, color, showScore }: ChartBoxProps) => {
  return (
    <Box key={type} flexDirection="column" justifyContent="center" alignItems="center" gap="s4">
      <Chart value={scorePercentage} valueCircleColor={color} showScore={showScore} />
      <Text.Normal size="100" weight="medium">
        {getUsersLabel(ParticipantTypes[type] ?? '')}
      </Text.Normal>
    </Box>
  )
}

type EnpsInsightKey = keyof EngagementInsightsEnpsInsight

const defaultData: EngagementInsightsEnpsInsight = {
  score: 0,
  participants: 0,
  respondents: 0,
  detractors: 0,
  passives: 0,
  promoters: 0,
  enoughAnswers: false,
  formStatus: 'draft',
  formId: 0,
  formStartOn: new Date().toDateString(),
  updatedAt: new Date(),
}

const EnpsWidgetStats = () => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { enps, managerId } = useEngagementInsightsData()

  const { loading, enpsInsight } = useEnpsInsight({ managerId })

  const enpsInsightsData = !!enpsInsight ? enpsInsight : defaultData
  const { participants, respondents, promoters, detractors } = enpsInsightsData
  const score = getScore(promoters, detractors, respondents)

  const chartColorsList = Object.entries(chartColors)
  const tagColor = getTagColor(enps)
  const tagLabel = getTagLabel(enps)

  const hasEnoughAnswers = enpsInsight?.enoughAnswers || false
  const showEllipse = hasEnoughAnswers && enps.isActive
  const showTag = !(showEllipse && !enps.isScheduled)

  const ellipseGradientColor = getEnpsWidgetEllipseColor(score)
  const scoreDescription = getScoreDescription(getScoreRange(score))

  const handleClick = () => {
    navigate(navigation.engagement.enps.results.fullPath())
    track('Go to eNPS')
  }

  return (
    <Box
      borderRadius={{
        all: 'abs008',
      }}
      border={{
        all: {
          color: 'grey400',
          width: 's1',
          style: 'solid',
        },
      }}
      style={{ flex: '1 1 0', position: 'relative', overflow: 'hidden', minHeight: '470px' }}
      padding="s24"
      cursor="pointer"
      onClick={() => handleClick()}
    >
      <WidgetHeader
        loading={loading}
        showEllipse={showEllipse}
        showTag={showTag}
        tagColor={tagColor}
        tagLabel={tagLabel}
        title={i18n.t('engagement_insights.enps_widget.empty_status.title')}
        ellipseGradientColor={ellipseGradientColor}
      />
      {loading && (
        <Box alignItems="center" justifyContent="center" height="full">
          <Spinner />
        </Box>
      )}
      {!loading && (
        <>
          {hasEnoughAnswers ? (
            <AnalyticsDescription
              participants={participants}
              respondents={respondents}
              score={score}
              scoreDescription={scoreDescription}
            />
          ) : (
            <EmptyStatusDescription respondents={respondents} participants={participants} />
          )}
          <Box
            flexDirection="row"
            gap="s24"
            style={{ position: 'absolute', bottom: '24px', left: '24px' }}
          >
            {chartColorsList.map(([type, color]) => {
              const scorePercentage = getPercentageValue(
                enpsInsightsData[type as EnpsInsightKey],
                respondents
              )
              return (
                <ChartBox
                  key={type}
                  type={type}
                  scorePercentage={scorePercentage}
                  color={color}
                  showScore={hasEnoughAnswers}
                />
              )
            })}
          </Box>
        </>
      )}
    </Box>
  )
}

export default observer(EnpsWidgetStats)
